import {
  Box,
  Stack,
  Text,
  VStack,
  useToast,
  Heading,
  useBreakpointValue,
  Spinner,
  Portal,
  PortalProps,
} from "@chakra-ui/react";
import { createOnUpdateEvent, EditModalType } from "../../../utils";
import { useDialog } from "../../../Common/Dialog";
import { useLoading, useProgress } from "../../hooks/form";
import { useCallback, useMemo, useRef, useState } from "react";
import actions from "../../../actions";
import { useKydEvent } from "../hooks/useKydEvent";
import { matchPath, useLocation, useParams } from "react-router-dom";
import { pollForEventUpdateStatus as pollForStatus } from "../utils/poolForEventUpdateStatus";
import LaunchEventButton from "../../LaunchEventButton";
import EventDetailSections from "../sections/EventDetailSections";

const Detail = () => {
  const [loading, updateLoading] = useLoading<{
    blocking: boolean;
    event: boolean;
    tickettype: boolean;
  }>({
    blocking: false,
    event: false,
    tickettype: false,
  });
  const { event_id } = useParams();
  const location = useLocation();
  const [modalType, setModalType] = useState(EditModalType.INACTIVE);
  const [dirtyText, setDirtyText] = useState<Record<string, any>>({});

  const dialog = useDialog();
  const toast = useToast();
  const { kydEvent, loading: isEventLoading, refetch } = useKydEvent(event_id);

  const updateDirtyText = useCallback((update: any | string) => {
    if (update === "all") {
      setDirtyText({});
    } else {
      setDirtyText((l: any) => {
        const keys = Object.keys(update);
        keys.forEach((key) => {
          if (update[key] === null) {
            delete l[key];
          } else {
            l[key] = update[key];
          }
        });
        return { ...l };
      });
    }
  }, []);

  const pollForEventUpdateStatus = useCallback(() => {
    return pollForStatus({
      onError: (text) => dialog({ text }),
      refetch: () => refetch({ silent: true }),
      event_id,
    });
  }, [event_id, dialog, refetch]);

  const onUpdateEvent = useCallback(
    createOnUpdateEvent({
      dialog,
      event_id,
      pollForEventUpdateStatus,
      refetch,
      setLoading: (isLoading) => updateLoading("event", isLoading),
      setModalType,
      toast,
    }),
    [
      event_id,
      refetch,
      updateLoading,
      pollForEventUpdateStatus,
      dialog,
      toast,
      setModalType,
    ]
  );

  const onLaunchEvent = useCallback(
    async (eventId: string) => {
      updateLoading("blocking", true);
      try {
        const result = await actions.ADMIN.launchEvent(eventId);
        if (result && result.update_id) {
          const params = new URLSearchParams(window.location.search);
          params.set("update_id", result.update_id);

          const newUrl =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname +
            "?" +
            params.toString();
          window.history.pushState({ path: newUrl }, "", newUrl);

          const success = await pollForEventUpdateStatus();
          if (success) {
            toast({
              title:
                kydEvent?.status === "OPEN"
                  ? "✅ Event Updated"
                  : "✅ Event Launched",
              description:
                kydEvent?.status === "OPEN"
                  ? "Your event updates are complete"
                  : "Your event is now pending review",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          }
        } else {
          await refetch({ silent: true });
        }

        toast({
          title:
            kydEvent?.status === "OPEN"
              ? "✅ Event Updated"
              : "✅ Event Launched",
          description:
            kydEvent?.status === "OPEN"
              ? "Your event updates are complete"
              : "Your event is now pending review",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("blocking", false);
      updateDirtyText("all");
    },
    [event_id, pollForEventUpdateStatus, updateDirtyText, updateLoading, dialog]
  );

  const currentNavTabSections = useMemo(() => {
    if (!kydEvent || !kydEvent.nav) return [];

    const currentTab = kydEvent.nav.find((nav) =>
      matchPath(nav.url, location.pathname)
    );
    if (currentTab) {
      return currentTab.sections;
    }

    return [];
  }, [kydEvent]);

  return (
    <Stack spacing={4} position="relative" maxW="full">
      {/* EVENT DYNAMIC SECTIONS */}
      {!kydEvent?.nav ? (
        <VStack w="100%">
          <Spinner />
        </VStack>
      ) : null}
      <EventDetailSections
        isLoading={loading.blocking || loading.event}
        onUpdateEvent={onUpdateEvent}
        setModalType={setModalType}
        refetchEvent={refetch}
        modalType={modalType}
        sections={currentNavTabSections}
        pollForEventUpdateStatus={pollForEventUpdateStatus}
      />
      {/* end: EVENT DYNAMIC SECTIONS */}

      <LaunchEventButton
        kydEvent={kydEvent}
        loading={loading}
        onLaunchEvent={() => onLaunchEvent(event_id)}
      />

      {kydEvent?.status === "PENDING_APPROVAL" ? (
        <VStack
          bg="yellow.100"
          borderWidth={"1px"}
          borderColor={"gray.100"}
          roundedTop={"lg"}
          p={4}
          w="100%"
          shadow={"lg"}
          bottom={0}
          position={"sticky"}
        >
          <Text fontWeight={"bold"}>🔍 Pending Approval</Text>
        </VStack>
      ) : null}
    </Stack>
  );
};

export default Detail;
